// src/components/proposals/pagePacking.js
//
// Shared pagination for the sections whose content is unbounded. Every page in
// these templates is a fixed 794x1123 div rendered as exactly one Prince page;
// a section that outgrows one page becomes several such divs rather than one
// tall container left to fragment.
//
// Heights are ESTIMATED from the data, never measured in the DOM. The print
// path is renderToString — a static string with no layout pass — so nothing can
// be measured there, and a browser measurement would not be authoritative for
// Prince anyway. A pure function called by both the preview and the print
// render cannot disagree with itself, and that agreement is the property that
// matters. Estimates round up and the usable height is trimmed, so the failure
// direction is an under-filled page rather than a clipped one: whitespace is
// visible, clipping is not.

export const PAGE_H   = 1123;
export const HEADER_H = 48;
export const FOOTER_H = 36;
export const PAD_TOP    = 44;
export const PAD_BOTTOM = 52;

/* Trim on the usable height. Absorbs small differences between these estimates
   and what Prince actually lays out. */
const SAFETY = 0.96;

export const CONTENT_H =
  Math.floor((PAGE_H - HEADER_H - FOOTER_H - PAD_TOP - PAD_BOTTOM) * SAFETY);

/* Section label + heading + intro paragraph, first page of a section only. */
export const INTRO_H = 114;
/* The "(continued)" line carried by later pages. */
export const CONT_H = 31;

export const linesOf = (text, charsPerLine) =>
  Math.max(1, Math.ceil((String(text || "").length || 1) / charsPerLine));

/* Greedily pack height-tagged segments into pages.
 *
 * `keepWithNext` exists for band headings: a "One-Off Costs" bar stranded at
 * the foot of a page with its rows overleaf reads as a rendering fault, so a
 * segment marked that way is measured together with the one after it and moves
 * with it. Runs of them chain, so a heading never separates from its content.
 *
 * A single segment taller than a whole page is placed alone and allowed to
 * overflow — splitting one internally is out of scope, and the pre-flight
 * warning still reports it.
 */
export function packSegments(segments, { firstOffset = INTRO_H, contOffset = CONT_H,
                                         capacity = CONTENT_H } = {}) {
  const list = (segments || []).filter(Boolean);
  if (!list.length) return [[]];

  /* Height of segment i plus any run it must stay attached to. */
  const runHeight = (i) => {
    let h = 0, j = i;
    while (j < list.length) {
      h += list[j].h;
      if (!list[j].keepWithNext) break;
      j += 1;
    }
    return h;
  };

  const pages = [];
  let page = [];
  let used = firstOffset;

  for (let i = 0; i < list.length; i += 1) {
    const needed = list[i].keepWithNext ? runHeight(i) : list[i].h;
    if (page.length && used + needed > capacity) {
      pages.push(page);
      page = [];
      used = contOffset;
    }
    page.push(list[i]);
    used += list[i].h;
  }

  if (page.length) pages.push(page);
  return pages;
}
